import type { PaymentMethod } from '@contracts/payment/PaymentMethod';
import type { ClientForSelector } from '@src/clients/ClientSelectOption';
import type { ClientSummary } from '@src/clients/domain/Client';
import { useClientsSummary } from '@src/clients/hooks/useClientsSummary';
import { useListAllCreditCards } from '@src/clients/hooks/useListAllClientCreditCards';
import { CollapsibleSectionWithListener } from '@src/common/components/CollapsibleSectionWithListener';
import { Divider } from '@src/common/components/Divider';
import { Stack } from '@src/common/components/Stack';
import { Toggle } from '@src/common/components/Toggle';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { ClientSelectionSection } from '@src/orders/ClientSelectionSection';
import { BillToDropdown } from '@src/orders/components/billTo/BillToDropdown';
import { duplicateOrderEvents } from '@src/orders/DuplicateOrderEvents';
import { PaymentMethodBanner } from '@src/orders/PaymentMethodBanner';
import { t } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ClientInformationSectionProps {
  defaultClientId?: string;
  readonly?: boolean;
  onClientChange?: (client: ClientSummary | null) => void;
  onThirdPartyChange?: (thirdPartyId: string | undefined) => void;
  onThirdPartyBillingChange?: (enabled: boolean) => void;
  isClientUser?: boolean;
}

export const ClientInformationSection = ({
  defaultClientId,
  readonly,
  onClientChange,
  onThirdPartyChange,
  onThirdPartyBillingChange,
  isClientUser,
}: ClientInformationSectionProps) => {
  const { t } = useTranslation();
  const [selectedClient, setSelectedClient] = useState<ClientSummary | null>(
    null,
  );
  const [thirdPartyBilling, setThirdPartyBilling] = useState(false);
  const [selectedThirdPartyId, setSelectedThirdPartyId] = useState<
    string | undefined
  >(undefined);

  const { clientList, isLoading: loadingClients } = useClientsSummary();

  const handleClientChange = (client: ClientForSelector | null) => {
    if (!client) {
      onClientChange?.(null);
      onThirdPartyChange?.(undefined);
      onThirdPartyBillingChange?.(false);
      return;
    }

    const matchingActiveClient = clientList.find(client.id);
    if (matchingActiveClient) {
      const defaultBillToClientId = matchingActiveClient.defaultBillToClientId;
      setSelectedClient(matchingActiveClient);
      setThirdPartyBilling(!!defaultBillToClientId);
      setSelectedThirdPartyId(defaultBillToClientId);
      onClientChange?.(matchingActiveClient);
      onThirdPartyBillingChange?.(!!defaultBillToClientId);
      onThirdPartyChange?.(defaultBillToClientId);
    }
  };

  const handleThirdPartyBillingToggle = () => {
    if (!readonly) {
      const newValue = !thirdPartyBilling;
      setThirdPartyBilling(newValue);
      setSelectedThirdPartyId(undefined);

      onThirdPartyBillingChange?.(newValue);
      onThirdPartyChange?.(undefined);
    }
  };

  const handleThirdPartyIdChange = (id: string) => {
    if (!readonly) {
      setSelectedThirdPartyId(id);
      onThirdPartyChange?.(id);
    }
  };
  const selectedThirdPartyClient = clientList.clients.find(
    (c) => c.id === selectedThirdPartyId,
  );

  return (
    <>
      <CollapsibleSectionWithListener
        title={t('clientInformation')}
        subtitle={t('clientInformationInstructions')}
        collapseEventName={duplicateOrderEvents.collapseEventName}
        uncollapseEventName={duplicateOrderEvents.uncollapseEventName}
      >
        <Stack direction="column" gap={theme.spacing['12']}>
          <ClientSelectionSection
            readonly={readonly}
            isLoading={loadingClients}
            activeClients={clientList.toSelector()}
            defaultClient={defaultClientId}
            onClientChange={handleClientChange}
          />

          <ThirdPartyBilling
            checked={thirdPartyBilling}
            disabled={readonly}
            onToggle={handleThirdPartyBillingToggle}
            clientId={selectedThirdPartyId}
            onChange={({ id }) => handleThirdPartyIdChange(id)}
            isAgencyClient={selectedClient?.isAgencyClient ?? false}
          />
          <PaymentMethodSection
            client={selectedClient}
            isThirdPartyPayment={thirdPartyBilling}
            isClientUser={isClientUser}
            thirdParty={selectedThirdPartyClient}
          />
        </Stack>
      </CollapsibleSectionWithListener>
    </>
  );
};

const title = css(() => ({
  fontWeight: 500,
}));

type ThirdPartyBillingProps = {
  checked: boolean;
  disabled: undefined | boolean;
  onToggle: () => void;
  clientId: string | undefined;
  onChange: ({ id }: { id: string }) => void;
  isAgencyClient: boolean;
};
const ThirdPartyBilling = (props: ThirdPartyBillingProps) => {
  return (
    <Stack direction="column" gap={theme.spacing['3']}>
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="column" gap={theme.spacing['3']}>
          <Typography className={title}>{t('thirdPartyBilling')}</Typography>
          <Typography>{t('thirdPartyBillingDescription')}</Typography>
        </Stack>
        <Toggle
          data-testid="third-party-billing-toggle"
          sx={{ marginBottom: 4 }}
          checked={props.isAgencyClient || props.checked}
          disabled={props.disabled || props.isAgencyClient}
          onToggle={props.onToggle}
          label={t('yes')}
        />
      </Stack>
      {(props.checked || props.isAgencyClient) && (
        <Stack direction="column" gap={theme.spacing['3']}>
          <Divider />
          <BillToDropdown
            disabled={props.disabled || props.isAgencyClient}
            clientId={props.clientId}
            onChange={props.onChange}
            label={t('client')}
          />
        </Stack>
      )}
    </Stack>
  );
};

type PaymentMethodSectionProps = {
  client: ClientSummary | null;
  isThirdPartyPayment: boolean;
  isClientUser?: boolean;
  thirdParty: ClientSummary | undefined;
};

const PaymentMethodSection = (props: PaymentMethodSectionProps) => {
  if (!props.client) return;

  const paymentMethod = (
    props.isThirdPartyPayment && props.thirdParty
      ? props.thirdParty.paymentMethod
      : props.client.paymentMethod
  ) as PaymentMethod | undefined;

  if (!paymentMethod) return;

  const payerId =
    props.isThirdPartyPayment && props.thirdParty
      ? props.thirdParty.id
      : props.client.id;

  return (
    <>
      {paymentMethod === 'CREDIT_CARD' ? (
        <PaymentMethodBannerWithCreditCards
          paymentMethod={paymentMethod}
          isClientUser={props.isClientUser ? props.isClientUser : false}
          clientId={payerId}
        />
      ) : (
        <PaymentMethodBanner
          paymentMethod={paymentMethod}
          isClientUser={props.isClientUser ? props.isClientUser : false}
          hasCreditCard={false}
        />
      )}
    </>
  );
};

const PaymentMethodBannerWithCreditCards = ({
  paymentMethod,
  isClientUser,
  clientId,
}: {
  paymentMethod: PaymentMethod;
  isClientUser: boolean;
  clientId: string;
}) => {
  const creditCardsData = useListAllCreditCards({ clientId });
  const hasCreditCards = creditCardsData.availableCreditCards.length > 0;
  return (
    <PaymentMethodBanner
      paymentMethod={paymentMethod}
      isClientUser={isClientUser}
      hasCreditCard={hasCreditCards}
    />
  );
};
